import type {
  IMenuDraft,
  IPublishResponse,
  ISyncCounts,
  TMenuImportKind,
} from '@/interfaces/IMenuImport';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import { languageName } from '@/domain/store-setup/display-names';
import { currencyPluralName } from '@/domain/store-setup/money';
import { storeLinkLabel } from '@/domain/store-setup/store-identity';
import {
  FIVE_ITEMS_CONVERSION,
  FIVE_ITEMS_THRESHOLD,
  MENU_PUBLISHED_CONVERSION,
} from '@/domain/store-setup/store-setup-analytics';
import type { IAdsConversion } from '@/domain/store-setup/store-setup-analytics';
import { isUpdateDraft, liveSummaryCounts, publishTotal } from './live-update';
import { draftSummary } from './menu-draft';

/**
 * «¿Publicamos su menú?» (plan §4.7): what the publish dialog tells the owner
 * before publishing, and what a successful publish reports (GA, Ads, the
 * store's currency and language). The dialog only renders these.
 */

/** Name of the dynamic modal (`useModal().showDynamicDialog`). */
export const PUBLISH_DIALOG_MODAL = 'v2-menu-publish';

/** What one press of «Publicar» ended with, as the dialog handles it. */
export type TPublishAttempt =
  | 'published'
  | 'changed'
  | 'unresolved'
  | 'nothingVisible'
  | 'failed';

/** How the dialog closed with an answer (`review`: «Revisarlos»); a plain close resolves null. */
export type TPublishDialogResult =
  | 'published'
  | 'changed'
  | 'unresolved'
  | 'review';

/** Where the published dishes came from (GA `menu_published.source`). */
export type TPublishSource = 'read' | 'copy' | 'manual';

/** An update session: what publishing changes (`liveSummaryCounts`) and the dishes diners will see (`publishTotal`). */
export type TPublishUpdate = ISyncCounts & { kept: number; total: number };

/** The dialog's update lines of a draft; null outside update sessions. */
export function publishUpdateOf(draft: IMenuDraft): TPublishUpdate | null {
  if (!isUpdateDraft(draft)) return null;
  const counts = liveSummaryCounts(draft);
  return {
    priceChanges: counts.priceChanges,
    added: counts.added,
    hidden: counts.hidden,
    shown: counts.shown,
    kept: counts.kept,
    total: publishTotal(draft),
  };
}

export interface IPublishDialogInput {
  draft: IMenuDraft;
  /** Visible priced dishes the store already has. */
  existingItems: number;
  /** The store's link (`restaurant.stringId`). */
  slug: string;
  storeLanguageId: string | null;
  storeCurrency: string;
  /** The store shows hidden dishes greyed as «No disponible». */
  showDisabledItems: boolean;
  locale: string;
  /** Update sessions only. */
  update: TPublishUpdate | null;
}

export interface IPublishDialogModel {
  /** «bistri.fi/{slug}». */
  link: string;
  /** Visible priced dishes of the draft (the API's `visiblePricedCount`). */
  draftVisible: number;
  existingItems: number;
  /** Dishes still marked «Revise». */
  unreviewed: number;
  /** Hidden dishes without a price. */
  hiddenUnpriced: number;
  showDisabledItems: boolean;
  /** Only when some dish has allergens. */
  allergens: { unconfirmed: number } | null;
  /** The menu's language, when publishing switches the store to it. */
  language: string | null;
  /** The answered currency, when publishing switches the store to it. */
  currency: string | null;
  /** Update sessions only. */
  update: TPublishUpdate | null;
}

export type TPublishLineId =
  | 'count'
  | 'prices'
  | 'added'
  | 'hidden'
  | 'shown'
  | 'kept'
  | 'unreviewed'
  | 'hiddenUnpriced'
  | 'allergens'
  | 'language'
  | 'currency';

export interface IPublishLine {
  id: TPublishLineId;
  key: string;
  /** Plural keys carry their count (rendered with `$tc`); null otherwise. */
  count: number | null;
  params: Record<string, string | number>;
  icon: string;
  /** `warning`: amber (something left to check). */
  tone: 'neutral' | 'warning';
}

function sameLanguage(a: string, b: string | null): boolean {
  return b !== null && a.toLowerCase() === b.toLowerCase();
}

/**
 * The facts of the dialog, taken when it opens. Language and currency only
 * change on a store without visible dishes (the API's writer, D23), so they
 * are only announced there. The unpriced and allergen numbers count only the
 * dishes publishing adds (`draftSummary`).
 */
export function publishDialogModel(
  input: IPublishDialogInput
): IPublishDialogModel {
  const { draft, existingItems, locale } = input;
  const summary = draftSummary(draft);
  const emptyStore = existingItems === 0;
  const language =
    emptyStore &&
    draft.languageId !== null &&
    !sameLanguage(draft.languageId, input.storeLanguageId)
      ? languageName(draft.languageId, locale)
      : null;
  const currency =
    emptyStore &&
    draft.currency !== null &&
    draft.currency !== input.storeCurrency
      ? currencyPluralName(draft.currency, locale)
      : null;
  return {
    link: storeLinkLabel(input.slug),
    draftVisible: summary.visiblePriced,
    existingItems,
    unreviewed: Math.max(0, summary.toCheck - summary.fix),
    hiddenUnpriced: summary.hiddenUnpriced,
    showDisabledItems: input.showDisabledItems,
    allergens: summary.allergensAny
      ? { unconfirmed: summary.allergensUnconfirmed }
      : null,
    language,
    currency,
    update: input.update,
  };
}

function countLine(
  id: TPublishLineId,
  key: string,
  count: number,
  icon: string
): IPublishLine {
  return { id, key, count, params: { count }, icon, tone: 'neutral' };
}

/** What an update changes on the menu (plan §3.6 lines 2–6), each only when it counts something. */
function updateLines(
  update: TPublishUpdate,
  showDisabledItems: boolean
): IPublishLine[] {
  const lines: IPublishLine[] = [];
  if (update.priceChanges > 0)
    lines.push(
      countLine(
        'prices',
        'menuImport.update.linePrices',
        update.priceChanges,
        'tag-outline'
      )
    );
  if (update.added > 0)
    lines.push(
      countLine(
        'added',
        'menuImport.update.lineAdded',
        update.added,
        'plus-circle-outline'
      )
    );
  if (update.hidden > 0)
    lines.push(
      countLine(
        'hidden',
        showDisabledItems
          ? 'menuImport.update.lineHiddenShown'
          : 'menuImport.update.lineHidden',
        update.hidden,
        'eye-off-outline'
      )
    );
  if (update.shown > 0)
    lines.push(
      countLine(
        'shown',
        'menuImport.update.lineShown',
        update.shown,
        'eye-outline'
      )
    );
  if (update.kept > 0)
    lines.push(
      countLine(
        'kept',
        'menuImport.update.lineKept',
        update.kept,
        'check-circle-outline'
      )
    );
  return lines;
}

/** The lines of the dialog, each only when it applies, in the §4.7 (and update §3.6) order. */
export function publishLines(model: IPublishDialogModel): IPublishLine[] {
  const lines: IPublishLine[] = [];
  const count = model.update
    ? model.update.total
    : model.existingItems + model.draftVisible;
  lines.push({
    id: 'count',
    key: 'menuImport.publish.count',
    count,
    params: { count, url: model.link },
    icon: 'eye-outline',
    tone: 'neutral',
  });
  if (model.update)
    lines.push(...updateLines(model.update, model.showDisabledItems));
  if (model.unreviewed > 0)
    lines.push({
      id: 'unreviewed',
      key: 'menuImport.publish.unreviewed',
      count: model.unreviewed,
      params: { count: model.unreviewed },
      icon: 'alert-outline',
      tone: 'warning',
    });
  if (model.hiddenUnpriced > 0)
    lines.push({
      id: 'hiddenUnpriced',
      key: model.showDisabledItems
        ? 'menuImport.publish.hiddenUnpricedShown'
        : 'menuImport.publish.hiddenUnpriced',
      count: model.hiddenUnpriced,
      params: { count: model.hiddenUnpriced },
      icon: 'eye-off-outline',
      tone: 'neutral',
    });
  if (model.allergens !== null)
    lines.push(
      model.allergens.unconfirmed > 0
        ? {
            id: 'allergens',
            key: 'menuImport.publish.allergensUnconfirmed',
            count: model.allergens.unconfirmed,
            params: { count: model.allergens.unconfirmed },
            icon: 'alert-circle-outline',
            tone: 'neutral',
          }
        : {
            id: 'allergens',
            key: 'menuImport.publish.allergensAll',
            count: null,
            params: {},
            icon: 'information-outline',
            tone: 'neutral',
          }
    );
  // An update never changes the store's language or currency.
  if (model.update === null && model.language !== null)
    lines.push({
      id: 'language',
      key: 'menuImport.publish.language',
      count: null,
      params: { language: model.language },
      icon: 'translate',
      tone: 'neutral',
    });
  if (model.update === null && model.currency !== null)
    lines.push({
      id: 'currency',
      key: 'menuImport.publish.currency',
      count: null,
      params: { currency: model.currency },
      icon: 'cash',
      tone: 'neutral',
    });
  return lines;
}

/** A copy session, dishes the AI read, or a menu built by hand. */
export function publishSource(
  kind: TMenuImportKind,
  draft: IMenuDraft
): TPublishSource {
  if (kind === 'copy') return 'copy';
  return Object.values(draft.items).some((item) => item.origin === 'ai')
    ? 'read'
    : 'manual';
}

export interface IPublishedEventInput {
  source: TPublishSource;
  hiddenUnpriced: number;
  entry: 'setup' | 'import';
}

/** The toast after a publish (plan §3.8). */
export interface IPublishedToast {
  key: string;
  /** An update that changed the menu: «Deshacer» can be offered. */
  undoable: boolean;
}

/**
 * «Su menú ya estaba al día.» (an update that changed nothing), «Listo, su
 * menú está actualizado.» (an update), else «Su menú ya está publicado.».
 */
export function publishedToast(response: IPublishResponse): IPublishedToast {
  const sync = response.sync;
  if (!sync) return { key: 'menuImport.publishedToast', undoable: false };
  const changed =
    sync.priceChanges + sync.added + sync.hidden + sync.shown > 0 ||
    response.counts.items > 0;
  return changed
    ? { key: 'menuImport.update.doneToast', undoable: true }
    : { key: 'menuImport.update.upToDateToast', undoable: false };
}

/** GA `menu_published` (§4.13). */
export function publishedEvent(
  response: IPublishResponse,
  input: IPublishedEventInput
): Record<string, unknown> {
  const event: Record<string, unknown> = {
    items: response.counts.items,
    categories: response.counts.categories,
    source: input.source,
    mode: response.mode,
    hiddenUnpriced: input.hiddenUnpriced,
    entry: input.entry,
    firstMenuPublish: response.firstMenuPublish,
  };
  if (response.sync) {
    event.priceChanges = response.sync.priceChanges;
    event.added = response.sync.added;
    event.hidden = response.sync.hidden;
    event.shown = response.sync.shown;
  }
  return event;
}

/**
 * The Ads conversions of a publish (D28): «Menú publicado» on the store's
 * first publish, plus «Añadir 5 items al menú» when it shows at least 5
 * dishes. Later publishes, and replayed answers (reported by the first
 * one), fire nothing.
 */
export function publishConversions(
  response: IPublishResponse
): IAdsConversion[] {
  if (!response.firstMenuPublish || response.replayed === true) return [];
  return response.counts.visibleItems >= FIVE_ITEMS_THRESHOLD
    ? [MENU_PUBLISHED_CONVERSION, FIVE_ITEMS_CONVERSION]
    : [MENU_PUBLISHED_CONVERSION];
}

/** The store after a publish: the currency and languages the API answered (D23). */
export function restaurantAfterPublish(
  restaurant: IRestaurant,
  response: IPublishResponse
): IRestaurant {
  return {
    ...restaurant,
    currency: response.restaurant.currency,
    languageId: response.restaurant.languageId,
    languageIds: response.restaurant.languageIds ?? undefined,
  };
}
